import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Search, ChevronDown, X, RotateCcw } from 'lucide-react';
import { fyLabel, getMaxIndianFYStartYear } from '../../../utils/indianFY';

/* ─── Types ──────────────────────────────────────────────────────────────── */

export type FYQuarter = 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'Full';

export interface FilterBarProps {
  searchTerm: string;
  onSearchChange: (v: string) => void;

  allCurrencies: string[];
  selectedCurrencies: string[];       // empty = all
  onCurrencyChange: (v: string[]) => void;

  calendarYears: string[];            // [] = any
  onCalendarYearsChange: (v: string[]) => void;
  calendarMonth: string;              // "" = any, else "1"-"12"
  onCalendarMonthChange: (v: string) => void;

  fyYears: string[];                  // fyStartYear strings, e.g. ["2023","2024"]
  onFYYearsChange: (v: string[]) => void;
  defaultFYYears: string[];
  fyQuarter: FYQuarter | '';
  onFYQuarterChange: (v: FYQuarter | '') => void;

  hasAnyFilter: boolean;
  onClearAll: () => void;
}

/* ─── helpers ────────────────────────────────────────────────────────────── */

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const CALENDAR_YEARS: string[] = (() => {
  const max = new Date().getFullYear();
  const years: string[] = [];
  for (let y = max; y >= 2010; y--) years.push(String(y));
  return years;
})();

const MAX_FY = getMaxIndianFYStartYear();
const ALL_FY_OPTIONS: string[] = (() => {
  const opts: string[] = [];
  for (let y = MAX_FY; y >= 2011; y--) opts.push(String(y));
  return opts;
})();

const FY_QUARTERS: { value: FYQuarter; label: string }[] = [
  { value: 'Q1', label: 'Q1 (Apr–Jun)' },
  { value: 'Q2', label: 'Q2 (Jul–Sep)' },
  { value: 'Q3', label: 'Q3 (Oct–Dec)' },
  { value: 'Q4', label: 'Q4 (Jan–Mar)' },
  { value: 'Full', label: 'Full FY' },
];

/* ─── Reusable dropdown wrapper ─────────────────────────────────────────── */

interface DropdownProps {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  minWidth?: string;
  disabled?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  label, open, onToggle, children, minWidth = '14rem', disabled = false,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onToggle();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onToggle]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={onToggle}
        className={`
          flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border
          bg-white border-slate-200 text-slate-700 font-medium
          hover:border-slate-300 hover:bg-slate-50 transition-colors
          focus:outline-none focus:ring-2 focus:ring-blue-500/40
          disabled:opacity-50 disabled:cursor-not-allowed
          whitespace-nowrap
        `}
      >
        {label}
        <ChevronDown
          size={14}
          className={`text-slate-600 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div
          className="absolute top-full mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg py-1 overflow-auto"
          style={{ minWidth, maxHeight: '280px' }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

/* ─── Currency multi-select ─────────────────────────────────────────────── */

interface CurrencyDropdownProps {
  allCurrencies: string[];
  selected: string[];
  onChange: (v: string[]) => void;
}

const CurrencyDropdown: React.FC<CurrencyDropdownProps> = ({
  allCurrencies, selected, onChange,
}) => {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen(o => !o), []);

  const allSelected = selected.length === 0;
  const label = allSelected
    ? 'All currencies'
    : `${selected.length} currenc${selected.length === 1 ? 'y' : 'ies'}`;

  const handleToggle = (ccy: string) => {
    if (selected.includes(ccy)) {
      const next = selected.filter(c => c !== ccy);
      onChange(next);
    } else {
      onChange([...selected, ccy]);
    }
  };

  return (
    <Dropdown label={label} open={open} onToggle={toggle} minWidth="12rem">
      {/* All option */}
      <label className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
        <input
          type="checkbox"
          className="accent-blue-600"
          checked={allSelected}
          onChange={() => onChange([])}
          readOnly={allSelected}
        />
        <span className="text-slate-700 font-medium">All currencies</span>
      </label>
      <div className="border-t border-slate-100 my-1" />
      {allCurrencies.map(ccy => (
        <label key={ccy} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
          <input
            type="checkbox"
            className="accent-blue-600"
            checked={selected.includes(ccy)}
            onChange={() => handleToggle(ccy)}
          />
          <span className="text-slate-700">{ccy}</span>
        </label>
      ))}
    </Dropdown>
  );
};

/* ─── Indian FY multi-select ────────────────────────────────────────────── */

interface FYDropdownProps {
  selected: string[];
  onChange: (v: string[]) => void;
  defaultFYYears: string[];
}

const FYDropdown: React.FC<FYDropdownProps> = ({ selected, onChange, defaultFYYears }) => {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen(o => !o), []);

  const allSelected = selected.length === ALL_FY_OPTIONS.length;
  const label =
    selected.length === 0
      ? 'No FY selected'
      : selected.length === 1
      ? fyLabel(parseInt(selected[0], 10))
      : `${selected.length} FYs`;

  const handleToggle = (fy: string) => {
    if (selected.includes(fy)) {
      onChange(selected.filter(y => y !== fy));
    } else {
      onChange([...selected, fy]);
    }
  };

  const handleSelectAll = () => {
    onChange(allSelected ? [] : [...ALL_FY_OPTIONS]);
  };

  const handleReset = () => onChange([...defaultFYYears]);

  return (
    <Dropdown label={label} open={open} onToggle={toggle} minWidth="13rem">
      <div className="px-3 py-1.5 flex gap-2">
        <button
          type="button"
          onClick={handleSelectAll}
          className="text-xs text-blue-600 hover:underline"
        >
          {allSelected ? 'Deselect all' : 'Select all'}
        </button>
        <span className="text-slate-300">|</span>
        <button
          type="button"
          onClick={handleReset}
          className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-0.5"
        >
          <RotateCcw size={11} />
          Reset
        </button>
      </div>
      <div className="border-t border-slate-100 mb-1" />
      {ALL_FY_OPTIONS.map(fy => (
        <label key={fy} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
          <input
            type="checkbox"
            className="accent-blue-600"
            checked={selected.includes(fy)}
            onChange={() => handleToggle(fy)}
          />
          <span className="text-slate-700">{fyLabel(parseInt(fy, 10))}</span>
        </label>
      ))}
    </Dropdown>
  );
};

/* ─── Calendar Year multi-select ────────────────────────────────────────── */

const CalendarYearDropdown: React.FC<{
  selected: string[];
  onChange: (v: string[]) => void;
}> = ({ selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen(o => !o), []);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const allSelected = selected.length === 0;
  const label = allSelected ? 'Year' : selected.length === 1 ? selected[0] : `${selected.length} Years`;

  const handleToggle = (y: string) => {
    if (selected.includes(y)) onChange(selected.filter(v => v !== y));
    else onChange([...selected, y]);
  };

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={toggle} className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border bg-white border-slate-200 text-slate-700 font-medium hover:border-slate-300 hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/40 whitespace-nowrap">
        {label}
        <ChevronDown size={14} className={`text-slate-600 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg py-1 overflow-auto" style={{ minWidth: '8rem', maxHeight: '280px' }}>
          <label className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
            <input type="checkbox" className="accent-blue-600" checked={allSelected} onChange={() => onChange([])} readOnly={allSelected} />
            <span className="text-slate-700 font-medium">Any year</span>
          </label>
          <div className="border-t border-slate-100 my-1" />
          {CALENDAR_YEARS.map(y => (
            <label key={y} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
              <input type="checkbox" className="accent-blue-600" checked={selected.includes(y)} onChange={() => handleToggle(y)} />
              <span className="text-slate-700">{y}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Main FilterBar ─────────────────────────────────────────────────────── */

export const FilterBar: React.FC<FilterBarProps> = ({
  searchTerm, onSearchChange,
  allCurrencies, selectedCurrencies, onCurrencyChange,
  calendarYears, onCalendarYearsChange,
  calendarMonth, onCalendarMonthChange,
  fyYears, onFYYearsChange,
  defaultFYYears,
  fyQuarter, onFYQuarterChange,
  hasAnyFilter, onClearAll,
}) => {
  const [calMonthOpen, setCalMonthOpen] = useState(false);
  const [quarterOpen, setQuarterOpen] = useState(false);

  const toggleCalMonth = useCallback(() => setCalMonthOpen(o => !o), []);
  const toggleQuarter = useCallback(() => setQuarterOpen(o => !o), []);

  const calMonthLabel = calendarMonth
    ? MONTHS_SHORT[parseInt(calendarMonth, 10) - 1]
    : 'Month';
  const quarterLabel = fyQuarter
    ? FY_QUARTERS.find(q => q.value === fyQuarter)?.label ?? 'Quarter'
    : 'Quarter';

  return (
    <div className="flex flex-wrap items-center gap-2">

        {/* Search */}
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-600 pointer-events-none" />
          <input
            type="text"
            placeholder="Search clients…"
            value={searchTerm}
            onChange={e => onSearchChange(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-600"
            >
              <X size={13} />
            </button>
          )}
        </div>

        {/* Divider */}
        <div className="h-6 w-px bg-slate-200 hidden sm:block" />

        {/* Currency */}
        <CurrencyDropdown
          allCurrencies={allCurrencies}
          selected={selectedCurrencies}
          onChange={onCurrencyChange}
        />

        {/* Divider */}
        <div className="h-6 w-px bg-slate-200 hidden sm:block" />

        {/* Calendar Year (multi-select) */}
        <CalendarYearDropdown
          selected={calendarYears}
          onChange={v => { onCalendarYearsChange(v); if (v.length === 0) onCalendarMonthChange(''); }}
        />

        {/* Calendar Month */}
        <Dropdown
          label={calMonthLabel}
          open={calMonthOpen}
          onToggle={toggleCalMonth}
          minWidth="8rem"
          disabled={calendarYears.length === 0}
        >
          <button
            type="button"
            onClick={() => { onCalendarMonthChange(''); setCalMonthOpen(false); }}
            className="w-full text-left px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-50"
          >
            Any month
          </button>
          <div className="border-t border-slate-100 my-1" />
          {MONTHS_SHORT.map((m, i) => {
            const monthNum = String(i + 1);
            return (
              <button
                key={m}
                type="button"
                onClick={() => { onCalendarMonthChange(monthNum); setCalMonthOpen(false); }}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 ${calendarMonth === monthNum ? 'text-blue-600 font-semibold' : 'text-slate-700'}`}
              >
                {m}
              </button>
            );
          })}
        </Dropdown>

        {/* Divider */}
        <div className="h-6 w-px bg-slate-200 hidden sm:block" />

        {/* Indian FY */}
        <FYDropdown
          selected={fyYears}
          onChange={onFYYearsChange}
          defaultFYYears={defaultFYYears}
        />

        {/* FY Quarter */}
        <Dropdown
          label={quarterLabel}
          open={quarterOpen}
          onToggle={toggleQuarter}
          minWidth="11rem"
          disabled={fyYears.length === 0}
        >
          <button
            type="button"
            onClick={() => { onFYQuarterChange(''); setQuarterOpen(false); }}
            className="w-full text-left px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-50"
          >
            Any quarter (Full FY)
          </button>
          <div className="border-t border-slate-100 my-1" />
          {FY_QUARTERS.map(q => (
            <button
              key={q.value}
              type="button"
              onClick={() => { onFYQuarterChange(q.value); setQuarterOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 ${fyQuarter === q.value ? 'text-blue-600 font-semibold' : 'text-slate-700'}`}
            >
              {q.label}
            </button>
          ))}
        </Dropdown>

        {/* Clear all */}
        {hasAnyFilter && (
          <button
            type="button"
            onClick={onClearAll}
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-red-500 transition-colors px-2 py-1.5 rounded hover:bg-red-50"
          >
            <X size={14} />
            Clear
          </button>
        )}
    </div>
  );
};

export default FilterBar;
